import { CopyButton, Row } from "@gadgetinc/widgets";
import { ChangeLanguageIcon } from "@gadgetinc/widgets/src/icons/ChangeLanguageIcon";
import { useStyletron } from "baseui";
import { Button, KIND as ButtonKind, SIZE } from "baseui/button";
import { StatefulMenu } from "baseui/menu";
import { PLACEMENT, StatefulPopover } from "baseui/popover";
import { Eta } from "eta";
import { defaultTo, isString, mapValues, memoize, sortBy } from "lodash";
import { observer } from "mobx-react-lite";
import type { ReactNode } from "react";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { EsbuildTranspiler } from "shared-utils/src/ts-converter/EsbuildTranspiler";
import { DevelopmentEnvironmentName } from "state-trees/src/Environment";
import { prettifyCodeExample } from "state-trees/src/prettifyCodeExample";
import { CodeBlockFilename, CodeBlockTitle, CodeBlockTitleBar, CodeBlockTitleBarSpacer } from "web/src/components/markdown/CodeBlock";
import { CodeHighlight } from "web/src/components/markdown/CodeHighlight";
import { LanguageIcon, LanguageIndicator, languageLabel } from "web/src/components/markdown/LanguageIndicator";
import { DocsContext } from "../chrome/DocsContext";
import { CodeBlock, CodeBlockContainer, handleCopyWithToast } from "./CodeBlock";
import { renameTsToJs } from "./InlineTsFilePath";
import { OpenInGraphQLPlaygroundButton, OpenInJavaScriptPlaygroundButton } from "./OpenInPlaygroundButton";

type LanguageExampleMap = {
  [language: string]: string | { query: string; variables?: any };
};

const memoizedTransform = memoize(
  async (options: { code: string }) => {
    const transpiler = new EsbuildTranspiler();
    return await transpiler.transpile(options.code, true, {
      addActionTypeComments: true,
      addRouteTypeComment: true,
    });
  },
  (options) => JSON.stringify(options)
);

const memoizedMaybePrettify = memoize(
  (options: { code: string; language: string; prettify?: boolean }) => {
    const { code, language, prettify } = options;
    return defaultTo(prettify, true) ? prettifyCodeExample(code, language, 85) : code;
  },
  (options) => JSON.stringify(options)
);

const compileEjsTemplate = memoize(
  (options: { code: string; contextProps: Record<string, any> }) => {
    const { code, contextProps } = options;

    const eta = new Eta({ varName: "ctx" });
    return eta.renderString(code, contextProps);
  },
  (args) => JSON.stringify(args)
);

const useLanguageCodeSnippets = (props: {
  examples: LanguageExampleMap;
  prettify?: boolean;
  disableTranspiler?: boolean;
  disableCodeInjection?: boolean;
  useRawCode?: boolean;
}) => {
  const { examples, prettify, disableTranspiler, disableCodeInjection, useRawCode } = props;
  const { initializedTranspiler, currentApp } = useContext(DocsContext);
  const maybeInjectCurrentAppToCode = useCallback(
    (code: string) => (disableCodeInjection ? code : compileEjsTemplate({ code, contextProps: { currentApp } })),
    [currentApp, disableCodeInjection]
  );

  const [jsExample, setJsExample] = useState<string | undefined>(undefined);
  const [reactExample, setReactExample] = useState<string | undefined>(undefined);

  const transformedExamples = useMemo(() => {
    const prettifiedExamples = mapValues(examples, (example, language) => {
      if (isString(example)) {
        return memoizedMaybePrettify({ code: maybeInjectCurrentAppToCode(example), language, prettify });
      } else {
        return {
          query: memoizedMaybePrettify({ code: example.query, language: "graphql", prettify }),
          variables: example.variables,
        };
      }
    });

    if (Object.keys(prettifiedExamples).includes("typescript") && !Object.keys(prettifiedExamples).includes("javascript")) {
      prettifiedExamples.javascript = prettifiedExamples.typescript;
    }
    if (Object.keys(prettifiedExamples).includes("tsx") && !Object.keys(prettifiedExamples).includes("react")) {
      prettifiedExamples.react = prettifiedExamples.tsx;
    }

    return prettifiedExamples;
  }, [examples, prettify, maybeInjectCurrentAppToCode]);

  useEffect(() => {
    if (!useRawCode && initializedTranspiler && examples["typescript"]) {
      const tsCode = maybeInjectCurrentAppToCode(examples["typescript"] as string);

      if (!disableTranspiler) {
        memoizedTransform({ code: tsCode })
          .then((result) => {
            setJsExample(result);
          })
          .catch(console.error);
      } else {
        setJsExample(tsCode);
      }
    }
    if (!useRawCode && initializedTranspiler && examples["tsx"]) {
      const tsxCode = maybeInjectCurrentAppToCode(examples["tsx"] as string);
      if (!disableTranspiler) {
        memoizedTransform({ code: tsxCode })
          .then((result) => {
            setReactExample(result);
          })
          .catch(console.error);
      } else {
        setReactExample(tsxCode);
      }
    }
  }, [initializedTranspiler, examples, disableTranspiler, useRawCode, maybeInjectCurrentAppToCode]);

  return {
    initializedTranspiler,
    languageCodeSnippets: {
      ...transformedExamples,
      ...(jsExample ? { javascript: memoizedMaybePrettify({ code: jsExample, language: "javascript", prettify }) } : {}),
      ...(reactExample ? { react: memoizedMaybePrettify({ code: reactExample, language: "react", prettify }) } : {}),
    },
  };
};

const isJavaScriptLanguage = (language: string) => {
  return language === "javascript" || language === "typescript";
};

const isReactLanguage = (language: string) => {
  return language === "react" || language === "tsx" || language === "jsx";
};

const isTypeScriptRelatedLanguage = (language: string) => {
  return language === "typescript" || language === "tsx";
};

const isPreactLanguage = (language: string) => {
  return language === "preact";
};

const getAvailableLanguages = (languageCodeSnippets: LanguageExampleMap, showTsLanguage: boolean) => {
  const languages = Object.keys(languageCodeSnippets);
  let filteredLanguages = languages;

  if (languages.some(isTypeScriptRelatedLanguage)) {
    filteredLanguages = filteredLanguages.filter((lang) => {
      const isJsOrReact = isJavaScriptLanguage(lang) || isReactLanguage(lang);
      if (!isJsOrReact) return true;
      return showTsLanguage ? isTypeScriptRelatedLanguage(lang) : !isTypeScriptRelatedLanguage(lang);
    });
  }

  return sortBy(filteredLanguages, [
    (lang) => {
      if (isJavaScriptLanguage(lang) || isPreactLanguage(lang)) return 0;
      if (isReactLanguage(lang)) return 1;
      return 2;
    },
  ]);
};

export const TabbedCodeExample = observer(
  (props: {
    title?: ReactNode;
    filename?: string;
    examples: LanguageExampleMap;
    detachSelector?: boolean;
    showGqlPlaygroundLink?: boolean;
    showJsPlaygroundLink?: boolean;
    isAutoSelector?: boolean;
    prettify?: boolean;
    disableTranspiler?: boolean;
    useRawCode?: boolean;
    disableEjs?: boolean;
  }) => {
    const [css, $theme] = useStyletron();
    const { currentApp, showTsLanguage } = useContext(DocsContext);
    const { languageCodeSnippets }: { languageCodeSnippets: LanguageExampleMap } = useLanguageCodeSnippets({ ...props });

    const availableLanguages = useMemo(
      () => getAvailableLanguages(languageCodeSnippets, showTsLanguage),
      [languageCodeSnippets, showTsLanguage]
    );

    const [selectedLanguage, setSelectedLanguage] = useState(availableLanguages[0]);

    useEffect(() => {
      if (availableLanguages.length > 0 && !availableLanguages.includes(selectedLanguage)) {
        setSelectedLanguage(availableLanguages[0]);
      }
    }, [availableLanguages, selectedLanguage]);

    const shouldShowMenu = availableLanguages.length > 1;
    const filename = props.filename ? renameTsToJs(props.filename, showTsLanguage) : undefined;

    const menuItems = availableLanguages.map((language) => ({
      label: language,
      onClick: () => setSelectedLanguage(language),
    }));

    const copyCode = useCallback(() => {
      const value = languageCodeSnippets[selectedLanguage];
      const code = isString(value) ? value : value.query;
      void handleCopyWithToast(code);
    }, [selectedLanguage, languageCodeSnippets]);

    let codeForDocsContentExtraction: string | undefined;
    let languageForDocsContentExtraction: string | undefined;
    if (languageCodeSnippets["typescript"]) {
      const tsSnippet = languageCodeSnippets["typescript"];
      codeForDocsContentExtraction = isString(tsSnippet) ? tsSnippet : tsSnippet?.query;
      languageForDocsContentExtraction = "typescript";
    } else if (languageCodeSnippets["javascript"]) {
      const jsSnippet = languageCodeSnippets["javascript"];
      codeForDocsContentExtraction = isString(jsSnippet) ? jsSnippet : jsSnippet?.query;
      languageForDocsContentExtraction = "javascript";
    } else if (languageCodeSnippets["tsx"]) {
      const tsxSnippet = languageCodeSnippets["tsx"];
      codeForDocsContentExtraction = isString(tsxSnippet) ? tsxSnippet : tsxSnippet?.query;
      languageForDocsContentExtraction = "tsx";
    } else if (languageCodeSnippets["react"]) {
      const reactSnippet = languageCodeSnippets["react"];
      codeForDocsContentExtraction = isString(reactSnippet) ? reactSnippet : reactSnippet?.query;
      languageForDocsContentExtraction = "react";
    }

    return (
      <CodeBlockContainer rawCode={codeForDocsContentExtraction} language={languageForDocsContentExtraction}>
        <CodeBlockTitleBar>
          {props.title && <CodeBlockTitle>{props.title}</CodeBlockTitle>}
          {filename && <CodeBlockFilename>{filename}</CodeBlockFilename>}
          <CodeBlockTitleBarSpacer />
          {shouldShowMenu ? (
            <StatefulPopover
              placement={PLACEMENT.bottom}
              overrides={{
                Body: {
                  style: {
                    backgroundColor: $theme.colors.primary600,
                    margin: $theme.sizing.scale500,
                    borderRadius: $theme.borders.radius200,
                  },
                },
              }}
              content={({ close }) => (
                <StatefulMenu
                  overrides={{
                    Option: {
                      props: {
                        getItemLabel: (item: { label: string }) => (
                          <Row $gap={$theme.sizing.scale300}>
                            <LanguageIcon language={item.label} />
                            {languageLabel(item.label)}
                          </Row>
                        ),
                      },
                      style: { ...$theme.typography.MonoLabelXSmall },
                    },
                  }}
                  onItemSelect={({ item }) => {
                    setSelectedLanguage(item.label);
                    close();
                  }}
                  items={menuItems}
                />
              )}
              accessibilityType="menu"
            >
              <Button
                kind={ButtonKind.minimal}
                size={SIZE.mini}
                endEnhancer={<ChangeLanguageIcon $style={{ marginLeft: "-4px" }} />}
                overrides={{
                  BaseButton: {
                    style: ({ $theme }) => ({
                      color: $theme.colors.primary300,
                      ...$theme.typography.MonoLabelXSmall,
                      ":hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                    }),
                  },
                }}
              >
                {languageLabel(selectedLanguage)}
              </Button>
            </StatefulPopover>
          ) : (
            <LanguageIndicator $style={{ whiteSpace: "nowrap" }} color={$theme.colors.primary300}>
              {languageLabel(selectedLanguage)}
            </LanguageIndicator>
          )}
          <CopyButton onClick={copyCode} />
        </CodeBlockTitleBar>
        {Object.entries(languageCodeSnippets).map(([language, data]) => {
          const code = isString(data) ? data : data.query;
          const variables = isString(data) ? undefined : data.variables;

          return (
            <div
              key={language}
              className={css({
                display: selectedLanguage === language ? "block" : "none",
                overflow: "auto",
                // WebKit browsers (Chrome, Safari, Edge)
                "::-webkit-scrollbar": { height: $theme.sizing.scale200 },
                "::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                "::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(255, 255, 255, 0.4)",
                  borderRadius: $theme.sizing.scale900,
                },
                // Firefox
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(255, 255, 255, 0.4) transparent",
              })}
            >
              {props.showJsPlaygroundLink && isJavaScriptLanguage(language) && (
                <OpenInJavaScriptPlaygroundButton
                  code={code}
                  tabName={props.title?.toString() ?? "JavaScript Example"}
                  primaryDomain={currentApp.productionRenderURL}
                  environment={currentApp.environmentName ?? DevelopmentEnvironmentName}
                  $style={{ float: "right", marginRight: $theme.sizing.scale500 }}
                />
              )}
              {props.showGqlPlaygroundLink && language === "graphql" && (
                <OpenInGraphQLPlaygroundButton
                  graphql={code}
                  variables={variables}
                  tabName={props.title?.toString() ?? "GraphQL Example"}
                  primaryDomain={currentApp.productionRenderURL}
                  environment={currentApp.environmentName ?? DevelopmentEnvironmentName}
                  $style={{ float: "right", marginRight: $theme.sizing.scale500 }}
                />
              )}
              <div
                onClick={copyCode}
                className={css({ padding: `${$theme.sizing.scale400} ${$theme.sizing.scale400} ${$theme.sizing.scale600}` })}
              >
                <CodeHighlight language={language}>{code}</CodeHighlight>
              </div>
              {variables && (
                <CodeBlock title="Variables" language="json">
                  {prettifyCodeExample(JSON.stringify(variables), "json", 85)}
                </CodeBlock>
              )}
            </div>
          );
        })}
      </CodeBlockContainer>
    );
  }
);
